import { z } from "zod"
import {
  branchIdSchema,
  correlationIdSchema,
  entityIdSchema,
  legalEntityIdSchema,
} from "./identifiers.js"

const base = {
  requestVersion: z.literal(1),
  correlationId: correlationIdSchema,
  idempotencyKey: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/),
  legalEntityId: legalEntityIdSchema,
  branchId: branchIdSchema,
  requestId: entityIdSchema,
  revisionId: entityIdSchema,
}

export const purchaseRequestLineV1Schema = z
  .object({
    itemMasterId: entityIdSchema,
    locationId: entityIdSchema,
    quantityMilli: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
    unitCostMinor: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
  })
  .strict()

export const purchaseRequestCreateV1Schema = z
  .object({
    ...base,
    expectedVersion: z.literal(0),
    demandSource: z.enum(["manual", "reorder"]),
    neededBy: z.string().date(),
    currency: z.string().regex(/^[A-Z]{3}$/),
    budgetContext: z.string().trim().max(140).nullable(),
    lines: z.array(purchaseRequestLineV1Schema).min(1).max(100),
  })
  .strict()
  .superRefine((value, context) => {
    const keys = value.lines.map(
      (line) => `${line.itemMasterId}:${line.locationId}`,
    )
    if (new Set(keys).size !== keys.length) {
      context.addIssue({
        code: "custom",
        message: "Purchase Request lines must be unique.",
      })
    }
    if (
      value.lines.some(
        (line) => (line.quantityMilli * line.unitCostMinor) % 1000 !== 0,
      )
    ) {
      context.addIssue({
        code: "custom",
        message: "Purchase Request totals exceed supported precision.",
      })
    }
  })

export const purchaseRequestSubmitV1Schema = z
  .object({
    ...base,
    expectedVersion: z.number().int().positive(),
  })
  .strict()

export const purchaseRequestDecisionV1Schema = z
  .object({
    ...base,
    decisionId: entityIdSchema,
    expectedVersion: z.number().int().positive(),
    decision: z.enum(["approve", "reject"]),
    reasonCode: z.string().regex(/^[a-z][a-z0-9_]{1,63}$/),
  })
  .strict()

export const purchaseRequestCancelV1Schema = z
  .object({
    ...base,
    expectedVersion: z.number().int().positive(),
    reasonCode: z.string().regex(/^[a-z][a-z0-9_]{1,63}$/),
  })
  .strict()

export const purchaseRequestResultV1Schema = z
  .object({
    entityType: z.literal("purchase_request"),
    entityId: entityIdSchema,
    legalEntityId: legalEntityIdSchema,
    branchId: branchIdSchema,
    state: z.enum([
      "draft",
      "pending_approval",
      "approved",
      "rejected",
      "canceled",
    ]),
    version: z.number().int().positive(),
    demandSource: z.enum(["manual", "reorder"]),
    neededBy: z.string().date(),
    currency: z.string().regex(/^[A-Z]{3}$/),
    totalMinor: z.number().int().nonnegative(),
    policyId: entityIdSchema,
    policyVersion: z.number().int().positive(),
    budgetContextRequired: z.boolean(),
    materialRequest: z.string().min(1).max(240).nullable(),
    supplierCommitmentCreated: z.literal(false),
  })
  .strict()

export type PurchaseRequestCreateV1 = z.infer<
  typeof purchaseRequestCreateV1Schema
>
export type PurchaseRequestDecisionV1 = z.infer<
  typeof purchaseRequestDecisionV1Schema
>
